// Loop lanes — the main area. Each lane spans the full width of the loop; the
// beat lines you played sit across the whole lane and pulse as the white
// playhead sweeps over them. Beat lines are draggable to nudge their timing.
// Full per-layer controls live in a compact header bar.

import type { LayerSummary } from "../audio/looper.ts";
import { iconButton, setIcon } from "./icons.ts";

export interface LanesHandlers {
  onMute(id: string, muted: boolean): void;
  onSolo(id: string, solo: boolean): void;
  onLock(id: string, locked: boolean): void;
  onVolume(id: string, db: number): void;
  onPan(id: string, pan: number): void;
  onSend(id: string, amount: number): void;
  onDelete(id: string): void;
  onDuplicate(id: string): void;
  onSetLength(id: string, multiple: number): void;
  onRename(id: string, name: string): void;
  /** A beat line was dragged to a new position (0..1 of the loop). */
  onMarkMove(id: string, index: number, pos: number): void;
  /** A lane was dragged to a new index in the stack. */
  onReorder(id: string, toIndex: number): void;
  /** The empty-state "Record your first loop" button. */
  onEmptyRecord(): void;
}

export interface GridInfo {
  bars: number; // loop length in bars (base loop)
  beatsPerBar: number;
  quantDivs: number; // subdivisions per beat at the quantize grid (e.g. 16n = 4)
  quantOn: boolean;
}

export interface Lanes {
  element: HTMLElement;
  setLayers(layers: readonly LayerSummary[]): void;
  setProgress(p: number): void;
  setLevels(levels: ReadonlyMap<string, number>): void;
  /** Update the timeline grid (bars/beats/quant) drawn behind every lane. */
  setGrid(info: GridInfo): void;
}

interface LaneRef {
  el: HTMLElement;
  marks: { el: HTMLElement; pos: number }[];
  grid: HTMLElement;
  multiple: number;
}

const VOL_MIN = -40;
const VOL_MAX = 6;

export function createLanes(handlers: LanesHandlers): Lanes {
  const el = document.createElement("div");
  el.className = "lanes";

  const list = document.createElement("div");
  list.className = "lanes__list";
  const empty = document.createElement("div");
  empty.className = "lanes__empty";
  const emptyText = document.createElement("p");
  emptyText.className = "lanes__empty-text";
  emptyText.textContent = "No loops yet. Pick an instrument below, play something, then record.";
  const emptyBtn = document.createElement("button");
  emptyBtn.type = "button";
  emptyBtn.className = "btn btn--sm";
  emptyBtn.textContent = "Record your first loop";
  emptyBtn.addEventListener("click", () => handlers.onEmptyRecord());
  empty.append(emptyText, emptyBtn);
  const playhead = document.createElement("div");
  playhead.className = "lanes__playhead";
  el.append(playhead, list, empty);

  const lanes = new Map<string, LaneRef>();
  let prevProgress = 0;
  let knownIds = new Set<string>();
  let gridInfo: GridInfo = { bars: 4, beatsPerBar: 4, quantDivs: 4, quantOn: true };

  return {
    element: el,
    setLayers(layers) {
      list.replaceChildren();
      lanes.clear();
      empty.style.display = layers.length === 0 ? "" : "none";
      const anySolo = layers.some((l) => l.solo);
      const nextIds = new Set<string>();
      for (const layer of layers) {
        const ref = buildLane(layer, handlers, anySolo, list);
        applyGrid(ref.grid, gridInfo, ref.multiple);
        lanes.set(layer.id, ref);
        list.append(ref.el);
        nextIds.add(layer.id);
        // Briefly highlight a layer that's new since the last render.
        if (!knownIds.has(layer.id) && knownIds.size > 0) {
          ref.el.classList.add("is-new");
          window.setTimeout(() => ref.el.classList.remove("is-new"), 1200);
        }
      }
      knownIds = nextIds;
    },
    setGrid(info) {
      gridInfo = info;
      for (const ref of lanes.values()) applyGrid(ref.grid, gridInfo, ref.multiple);
    },
    setProgress(p) {
      playhead.style.left = `${Math.max(0, Math.min(1, p)) * 100}%`;
      // Pulse any beat line the playhead crossed since the last frame.
      const wrapped = p < prevProgress;
      for (const ref of lanes.values()) {
        for (const m of ref.marks) {
          const crossed = wrapped
            ? m.pos > prevProgress || m.pos <= p
            : prevProgress < m.pos && m.pos <= p;
          if (crossed) pulse(m.el);
        }
      }
      prevProgress = p;
    },
    setLevels(levels) {
      for (const [id, ref] of lanes) {
        ref.el.style.setProperty("--level", String(levels.get(id) ?? 0));
      }
    },
  };
}

function buildLane(layer: LayerSummary, handlers: LanesHandlers, anySolo: boolean, list: HTMLElement): LaneRef {
  const lane = document.createElement("div");
  lane.className = "lane";
  lane.dataset["kind"] = layer.kind;
  lane.style.setProperty("--lane-color", layer.color);
  lane.style.setProperty("--level", "0");
  if (layer.muted) lane.classList.add("is-muted");
  if (anySolo && !layer.solo) lane.classList.add("is-dimmed");
  if (layer.locked) lane.classList.add("is-locked");

  // Full-width activity field with the level bloom + the beat lines.
  const bloom = document.createElement("div");
  bloom.className = "lane__bloom";
  // Timeline grid (bars / beats / quant subdivisions) drawn behind the marks.
  const grid = document.createElement("div");
  grid.className = "lane__grid";
  const field = document.createElement("div");
  field.className = "lane__field";
  const marks: { el: HTMLElement; pos: number }[] = [];
  const draggable = layer.kind === "event" && !layer.locked;
  layer.marks.forEach((m, index) => {
    const mark = document.createElement("span");
    mark.className = "lane__mark";
    const pos = Math.max(0, Math.min(1, m.pos));
    mark.style.left = `${pos * 100}%`;
    mark.style.opacity = String(0.4 + Math.max(0, Math.min(1, m.velocity)) * 0.6);
    if (draggable) {
      mark.classList.add("is-draggable");
      mark.title = "Drag to move this hit";
      makeDraggable(mark, field, (newPos) => handlers.onMarkMove(layer.id, index, newPos));
    }
    field.append(mark);
    marks.push({ el: mark, pos });
  });

  // Compact header bar (overlaid on the field).
  const bar = document.createElement("div");
  bar.className = "lane__bar";
  const head = document.createElement("div");
  head.className = "lane__head";
  const drag = iconButton("drag", "Drag to reorder", "lane__drag");
  setupLaneReorder(lane, drag, list, layer.id, handlers.onReorder);
  const swatch = document.createElement("span");
  swatch.className = "lane__swatch";
  const name = document.createElement("span");
  name.className = "lane__name";
  name.textContent = layer.name;
  name.title = "Click to rename";
  name.addEventListener("click", () => startRename(name, layer, handlers));
  const renameBtn = iconButton("pencil", "Rename layer", "lane__sm lane__icon");
  renameBtn.addEventListener("click", () => startRename(name, layer, handlers));
  head.append(drag, swatch, name, renameBtn);

  const controls = document.createElement("div");
  controls.className = "lane__controls";
  controls.append(
    toggleBtn("S", layer.solo, "Solo", (on) => handlers.onSolo(layer.id, on)),
    toggleBtn("M", layer.muted, "Mute", (on) => handlers.onMute(layer.id, on)),
    miniSlider("Vol", VOL_MIN, VOL_MAX, 1, layer.volumeDb, (v) => handlers.onVolume(layer.id, v), fmtDb),
    miniSlider("Pan", -1, 1, 0.05, layer.pan, (v) => handlers.onPan(layer.id, v), fmtPan),
    miniSlider("FX", 0, 1, 0.05, layer.sendAmount, (v) => handlers.onSend(layer.id, v), fmtPct),
  );
  // Loop-length re-pick (event layers only): cycles 1× → 2× → 4×.
  if (layer.kind === "event") {
    const lengths = [1, 2, 4];
    const lenBtn = document.createElement("button");
    lenBtn.type = "button";
    lenBtn.className = "lane__sm lane__len";
    lenBtn.textContent = `${layer.multiple}×`;
    lenBtn.title = "Loop length (click to cycle 1x / 2x / 4x)";
    lenBtn.addEventListener("click", () => {
      const next = lengths[(lengths.indexOf(layer.multiple) + 1) % lengths.length] ?? 1;
      handlers.onSetLength(layer.id, next);
    });
    controls.append(lenBtn);
  }
  const dupBtn = iconButton("copy", "Duplicate layer", "lane__sm lane__icon");
  dupBtn.addEventListener("click", () => handlers.onDuplicate(layer.id));
  controls.append(
    dupBtn,
    lockBtn(layer.locked, (on) => handlers.onLock(layer.id, on)),
    deleteBtn(layer.locked, () => handlers.onDelete(layer.id)),
  );

  bar.append(head, controls);
  lane.append(bloom, grid, field, bar);
  return { el: lane, marks, grid, multiple: layer.multiple };
}

/**
 * Size the three gradient layers of a lane's grid to its loop. Bar / beat /
 * quant lines tile at their own spacing; opacity (set in CSS) makes the
 * hierarchy readable, and the quant layer only shows when quantize is on so you
 * can see hits snapping to the grid. A longer (2x/4x) layer scales its bars.
 */
function applyGrid(grid: HTMLElement, info: GridInfo, multiple: number): void {
  const bars = Math.max(1, Math.round(info.bars * Math.max(1, multiple)));
  const beats = Math.max(1, bars * Math.max(1, info.beatsPerBar));
  const quants = Math.max(beats, beats * Math.max(1, info.quantDivs));
  const pct = (n: number): string => `${100 / n}% 100%`;
  // Order matches the CSS background layers: bar, beat, quant.
  grid.style.backgroundSize = `${pct(bars)}, ${pct(beats)}, ${pct(quants)}`;
  grid.classList.toggle("is-quant", info.quantOn && info.quantDivs > 1);
}

function makeDraggable(mark: HTMLElement, field: HTMLElement, onCommit: (pos: number) => void): void {
  mark.addEventListener("pointerdown", (e) => {
    e.preventDefault();
    e.stopPropagation();
    mark.setPointerCapture(e.pointerId);
    mark.classList.add("is-dragging");
    const rect = field.getBoundingClientRect();
    let pos = 0;
    const move = (ev: PointerEvent): void => {
      pos = Math.max(0, Math.min(0.999, (ev.clientX - rect.left) / rect.width));
      mark.style.left = `${pos * 100}%`;
    };
    const up = (ev: PointerEvent): void => {
      mark.releasePointerCapture(ev.pointerId);
      mark.classList.remove("is-dragging");
      mark.removeEventListener("pointermove", move);
      mark.removeEventListener("pointerup", up);
      onCommit(pos);
    };
    move(e);
    mark.addEventListener("pointermove", move);
    mark.addEventListener("pointerup", up);
  });
}

function pulse(el: HTMLElement): void {
  el.classList.remove("is-hit");
  void el.offsetWidth;
  el.classList.add("is-hit");
}

function startRename(name: HTMLElement, layer: LayerSummary, handlers: LanesHandlers): void {
  const input = document.createElement("input");
  input.type = "text";
  input.className = "lane__rename";
  input.value = layer.name;
  name.replaceWith(input);
  input.focus();
  input.select();
  const commit = (): void => handlers.onRename(layer.id, input.value.trim() || layer.name);
  input.addEventListener("blur", commit);
  input.addEventListener("keydown", (e) => {
    if (e.key === "Enter") input.blur();
    else if (e.key === "Escape") {
      input.value = layer.name;
      input.blur();
    }
  });
}

function toggleBtn(label: string, initial: boolean, title: string, onChange: (on: boolean) => void): HTMLButtonElement {
  const btn = document.createElement("button");
  btn.type = "button";
  btn.className = "lane__sm";
  btn.textContent = label;
  btn.title = title;
  btn.setAttribute("aria-label", title);
  btn.classList.toggle("is-on", initial);
  btn.setAttribute("aria-pressed", String(initial));
  let on = initial;
  btn.addEventListener("click", () => {
    on = !on;
    btn.classList.toggle("is-on", on);
    btn.setAttribute("aria-pressed", String(on));
    onChange(on);
  });
  return btn;
}

function lockBtn(initial: boolean, onChange: (locked: boolean) => void): HTMLButtonElement {
  const btn = iconButton(initial ? "lock" : "unlock", initial ? "Locked. Protected." : "Lock layer", "lane__sm lane__icon");
  btn.classList.toggle("is-on", initial);
  btn.setAttribute("aria-pressed", String(initial));
  let on = initial;
  btn.addEventListener("click", () => {
    on = !on;
    setIcon(btn, on ? "lock" : "unlock");
    btn.classList.toggle("is-on", on);
    btn.setAttribute("aria-pressed", String(on));
    btn.title = on ? "Locked. Protected." : "Lock layer";
    onChange(on);
  });
  return btn;
}

function deleteBtn(locked: boolean, onConfirm: () => void): HTMLButtonElement {
  const btn = document.createElement("button");
  btn.type = "button";
  btn.className = "lane__btn lane__btn--del";
  btn.textContent = "Delete";
  if (locked) {
    btn.disabled = true;
    btn.title = "Unlock to delete";
    return btn;
  }
  let armed = false;
  btn.addEventListener("click", () => {
    if (!armed) {
      armed = true;
      btn.textContent = "Sure?";
      btn.classList.add("is-armed");
      window.setTimeout(() => {
        armed = false;
        btn.textContent = "Delete";
        btn.classList.remove("is-armed");
      }, 2000);
    } else {
      onConfirm();
    }
  });
  return btn;
}

/**
 * Pointer-drag a lane (via its handle) to reorder it. The grabbed lane lifts
 * and follows the pointer; the others slide to open a gap and animate into
 * place. On release we settle the DOM into the new order first, then commit, so
 * there's no flash when the looper re-emits.
 */
function setupLaneReorder(
  lane: HTMLElement,
  handle: HTMLElement,
  list: HTMLElement,
  id: string,
  onReorder: (id: string, toIndex: number) => void,
): void {
  handle.addEventListener("pointerdown", (e) => {
    e.preventDefault();
    handle.setPointerCapture(e.pointerId);

    const items = [...list.children] as HTMLElement[];
    const startIndex = items.indexOf(lane);
    const rect = lane.getBoundingClientRect();
    const gap = parseFloat(getComputedStyle(list).rowGap || "0") || 0;
    const step = rect.height + gap;
    const startY = e.clientY;
    let targetIndex = startIndex;

    lane.classList.add("is-grabbed");
    list.classList.add("is-reordering");

    const move = (ev: PointerEvent): void => {
      const dy = ev.clientY - startY;
      lane.style.transform = `translateY(${dy}px)`;
      targetIndex = Math.max(0, Math.min(items.length - 1, startIndex + Math.round(dy / step)));
      // Slide the lanes between the old and new slot to reveal the drop gap.
      items.forEach((el, i) => {
        if (el === lane) return;
        let shift = 0;
        if (startIndex < targetIndex && i > startIndex && i <= targetIndex) shift = -step;
        else if (startIndex > targetIndex && i >= targetIndex && i < startIndex) shift = step;
        el.style.transform = shift ? `translateY(${shift}px)` : "";
      });
    };

    const up = (ev: PointerEvent): void => {
      handle.releasePointerCapture(ev.pointerId);
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      lane.classList.remove("is-grabbed");
      list.classList.remove("is-reordering");
      for (const el of items) el.style.transform = "";
      if (targetIndex !== startIndex) {
        // Settle the DOM now so the upcoming re-render matches (no flash).
        const rest = items.filter((el) => el !== lane);
        const ref = rest[targetIndex] ?? null;
        list.insertBefore(lane, ref);
        onReorder(id, targetIndex);
      }
    };

    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  });
}

function miniSlider(
  label: string,
  min: number,
  max: number,
  step: number,
  value: number,
  onInput: (v: number) => void,
  fmt?: (v: number) => string,
): HTMLElement {
  const wrap = document.createElement("label");
  wrap.className = "lane__mini";
  const tag = document.createElement("span");
  tag.className = "lane__mini-label";
  tag.textContent = label;
  const slider = document.createElement("input");
  slider.type = "range";
  slider.min = String(min);
  slider.max = String(max);
  slider.step = String(step);
  slider.value = String(value);
  slider.className = "slider slider--mini";
  slider.setAttribute("aria-label", label);
  // Show the live value on the label while adjusting, then revert to the name.
  let revert = 0;
  slider.addEventListener("input", () => {
    const v = Number(slider.value);
    onInput(v);
    if (fmt) {
      tag.textContent = fmt(v);
      window.clearTimeout(revert);
      revert = window.setTimeout(() => (tag.textContent = label), 900);
    }
  });
  wrap.append(tag, slider);
  return wrap;
}

function fmtDb(v: number): string {
  if (v <= VOL_MIN) return "−∞";
  return `${v > 0 ? "+" : ""}${Math.round(v)}`;
}
function fmtPan(v: number): string {
  if (Math.abs(v) < 0.05) return "C";
  return `${v < 0 ? "L" : "R"}${Math.round(Math.abs(v) * 100)}`;
}
function fmtPct(v: number): string {
  return `${Math.round(v * 100)}`;
}
